import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Button,
    FieldError,
    Input,
    ListBox,
    Select,
    TextField,
    Typography,
} from '@heroui/react';
import type { BarcodeFixAction, BarcodeIssue } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import {
    fixOptionsOf,
    typedCodeStatusOf,
    type BarcodeIssueChoice,
} from './barcodeIssuesMeta';

type BarcodeIssueFixCellProps = {
    issue: BarcodeIssue;
    rowKey: string;
    choice: BarcodeIssueChoice;
    /** The refusal message the last fix call put on this row, if any. */
    error: string | null;
    onFixChange: (key: string, fix: BarcodeFixAction) => void;
    onTypedChange: (key: string, typed: string) => void;
    onUseAnyway: (key: string) => void;
    /** The row is the previous page's, shown while the next one loads. */
    isDisabled?: boolean;
};

const COPY = 'products.barcodeIssues';

/**
 * A row's fix (board `bc-Repair`): the select with the row's options, its
 * suggestion first; **Type a barcode** opens the digits field under it, with
 * the shared `components.barcodeEntry` copy: letters are refused inline and
 * an 8- or 13-digit code whose check digit doesn't match asks for **Use it
 * anyway**. A refusal the API blamed on this row shows under the select.
 */
export const BarcodeIssueFixCell: React.FC<BarcodeIssueFixCellProps> = ({
    issue,
    rowKey,
    choice,
    error,
    onFixChange,
    onTypedChange,
    onUseAnyway,
    isDisabled = false,
}) => {
    const { t } = useTranslation('pages');
    const { t: tc } = useTranslation('components');
    const status =
        choice.fix === 'type'
            ? typedCodeStatusOf(choice.typed, choice.usedAnyway)
            : null;
    const testId = `barcode-issues-fix-${rowKey}`;

    return (
        <div className="flex min-w-56 flex-col gap-2">
            <Select
                aria-label={t(`${COPY}.fixAria`, {
                    product: issue.productName,
                })}
                className="w-full"
                isDisabled={isDisabled}
                isInvalid={error !== null}
                onChange={(key) => {
                    if (key !== null) {
                        onFixChange(rowKey, String(key) as BarcodeFixAction);
                    }
                }}
                value={choice.fix}
            >
                <Select.Trigger {...getNodeTestIdProp(testId)}>
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <FieldError>{error}</FieldError>
                <Select.Popover>
                    <ListBox>
                        {fixOptionsOf(issue).map((fix) => (
                            <ListBox.Item
                                id={fix}
                                key={fix}
                                textValue={t(`${COPY}.fix.${fix}`)}
                            >
                                {t(`${COPY}.fix.${fix}`)}
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Select.Popover>
            </Select>
            {status === null ? null : (
                <TextField
                    aria-label={tc('barcodeEntry.label')}
                    className="flex w-full flex-col gap-1"
                    isDisabled={isDisabled}
                    isInvalid={status === 'notDigits'}
                    onChange={(value) => onTypedChange(rowKey, value)}
                    value={choice.typed}
                    {...getNodeTestIdProp(`${testId}-typed`)}
                >
                    <Input
                        inputMode="numeric"
                        placeholder={tc('barcodeEntry.placeholder')}
                    />
                    <FieldError>{tc('barcodeEntry.notDigits')}</FieldError>
                </TextField>
            )}
            {status === 'checkDigit' ? (
                <Alert
                    status="warning"
                    {...getNodeTestIdProp(`${testId}-check-digit`)}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Description>
                            {tc('barcodeEntry.checkDigit')}
                        </Alert.Description>
                    </Alert.Content>
                    <Button
                        isDisabled={isDisabled}
                        onPress={() => onUseAnyway(rowKey)}
                        size="sm"
                        variant="tertiary"
                    >
                        {tc('barcodeEntry.useAnyway')}
                    </Button>
                </Alert>
            ) : null}
            {status === 'usedAnyway' ? (
                <Typography color="muted" type="body-xs">
                    {tc('barcodeEntry.usedAnyway')}
                </Typography>
            ) : null}
        </div>
    );
};
